/**
 * Shared utilities for generating select options and formatting values.
 * Used by both section types and metric inputs.
 */

import type { SectionType } from '@az3fr/core';

import { sectionTypeDisplayInfosArray } from '@/entities/section-plan/model/section-type';

export interface SelectOptionConfig<T extends string | number = number> {
	value: T;
	label: string;

	disabled?: boolean;
	isGroupHeader?: boolean; // Used to style group headers in selects
}

// Generic cache that can store options of any valid type
// biome-ignore lint/suspicious/noExplicitAny: Cache stores multiple option types
const OPTIONS_CACHE = new Map<string, SelectOptionConfig<any>[]>();

// Type-safe helper functions for the generic cache
function getOptionsFromCache<T extends string | number>(
	key: string,
): SelectOptionConfig<T>[] | undefined {
	return OPTIONS_CACHE.get(key) as SelectOptionConfig<T>[] | undefined;
}

function setOptionsInCache<T extends string | number>(
	key: string,
	value: SelectOptionConfig<T>[],
): void {
	OPTIONS_CACHE.set(key, value);
}

// export interface RangeConfig {
//   min: number;
//   max: number;
//   step: number;
//   precision: number;
//   displayAs?: 'number' | 'time';
// }

/**
 * Format duration in seconds to a human readable string using symbols.
 * Supports decimal seconds for precise timing (e.g., rep pace).
 * - Under 1 minute: "55″" or "1.5″"
 * - 1 minute or more: "MM′:SS″" format ("01′:30″") or "01′:30.5″"
 * - Over 1 hour: "Hh:MM′:SS″" format ("1h:30′:00″") or "1h:30′:00.5″"
 */
export function formatDuration(duration: number): string {
	if (duration < 60) {
		// For sub-minute durations, show decimals if present
		return duration % 1 === 0 ? `${duration}″` : `${duration.toFixed(1)}″`;
	}

	const hours = Math.floor(duration / 3600);
	const minutes = Math.floor((duration % 3600) / 60);
	const remainingSeconds = duration % 60;

	const parts: string[] = [];

	if (hours > 0) {
		parts.push(`${hours}h`);
	}
	if (minutes > 0 || hours > 0) {
		parts.push(`${minutes.toString().padStart(2, '0')}′`);
	}

	// Format seconds with decimal precision when needed
	const secondsStr =
		remainingSeconds % 1 === 0
			? remainingSeconds.toString().padStart(2, '0')
			: remainingSeconds.toFixed(1).padStart(4, '0'); // e.g., "01.5" for 1.5 seconds

	parts.push(`${secondsStr}″`);

	return parts.join(':');
}

/**
 * Generate repetition options (sets/rounds) from 1 to 200
 * These are the numbers that can choose from in the ui for sets/rounds of a section.
 */
export function generateRepetitionOptions(): SelectOptionConfig<number>[] {
	const cacheKey = 'repetition-1-200';
	const cached = getOptionsFromCache<number>(cacheKey);

	if (cached) {
		return cached;
	}

	const options: SelectOptionConfig<number>[] = [];
	for (let i = 1; i <= 200; i += 1) {
		options.push({
			value: i,
			label: i.toString(),
		});
	}

	setOptionsInCache(cacheKey, options);
	return options;
}

/**
 * Generate interval options from 1 to 10 minutes in seconds
 *
 * Ranges:
 * - 30-120 seconds: 1 second steps (90 options)
 * - 2:05-5:00 minutes: 5 second steps (36 options)
 * - 5:15-10:00 minutes: 15 second steps (20 options)
 *
 * Total: 90 + 36 + 20 = 146 options
 */
export function generateIntervalOptions(): SelectOptionConfig<number>[] {
	const cacheKey = 'interval-30-600';
	const cached = getOptionsFromCache<number>(cacheKey);

	if (cached) {
		return cached;
	}

	const options: SelectOptionConfig<number>[] = [];
	const steps = [
		{ upTo: 120, step: 1 }, // from 30 seconds up to 2 minutes, step 1 second
		{ upTo: 5 * 60, step: 5 }, // from 2 minutes up to 5 minutes, step 5 seconds
		{ upTo: 10 * 60 + 15, step: 15 }, // from 5 minutes up to 10 minutes, step 15 seconds
	];

	// start at 30 seconds
	let current = 30;
	for (const range of steps) {
		while (current < range.upTo) {
			options.push({ value: current, label: formatDuration(current) });
			current += range.step;
		}
	}

	setOptionsInCache(cacheKey, options);
	return options;
}

/**
 * Generate duration options based on an interval if it is provided
 *
 * if interval is provided, the options will be from 1 to 200 times the interval
 * If no interval is provided, the options will be from 1min to 10hours in seconds
 * the label will be in minutes but the value will be in seconds
 */
export function generateDurationOptions(interval?: number | null): SelectOptionConfig<number>[] {
	// For EMOM, we need to generate options based on the interval
	if (interval !== undefined && interval !== null) {
		if (interval <= 0) {
			throw new Error('Interval must be greater than 0');
		}

		const cacheKey = `duration-emom-${interval}`;
		const cached = getOptionsFromCache<number>(cacheKey);

		if (cached) {
			return cached;
		}

		const options: SelectOptionConfig<number>[] = [];
		for (let rounds = 1; rounds <= 200; rounds += 1) {
			const totalSeconds = rounds * interval;
			options.push({
				value: totalSeconds,
				label: `${rounds}x = ${formatDuration(totalSeconds)}`,
			});
		}

		setOptionsInCache(cacheKey, options);
		return options;
	}

	// For AMRAP, Generates a generic absolute time range.
	const cacheKey = 'duration-amrap';
	const cached = getOptionsFromCache<number>(cacheKey);

	if (cached) {
		return cached;
	}

	const options: SelectOptionConfig<number>[] = [];
	const steps = [
		{ upTo: 10 * 60, step: 15 }, // up to 10 minutes, step 15 seconds
		{ upTo: 30 * 60, step: 30 }, // up to 30 minutes, step 30 seconds
		{ upTo: 5 * 60 * 60, step: 60 }, // up to 5 hours, step 60 seconds
	];

	// start at 2 minutes
	let current = 1 * 60;
	for (const range of steps) {
		while (current < range.upTo) {
			options.push({ value: current, label: formatDuration(current) });
			current += range.step;
		}
	}

	setOptionsInCache(cacheKey, options);
	return options;
}

/**
 * Generate duration options with variable step sizes for metrics.
 *
 * Ranges:
 * - 1-60 seconds: 1 second steps (60 options)
 * - 1:05-5:00 minutes: 5 second steps (48 options)
 * - 5:15-60:00 minutes: 15 second steps (220 options)
 * - 1:01:00-10:00:00 hours: 1 minute steps (540 options)
 *
 * Total: ~868 options with virtual scrolling support
 */
export function generateMetricDurationOptions(): SelectOptionConfig<number>[] {
	const cacheKey = 'metric-duration';
	const cached = getOptionsFromCache<number>(cacheKey);

	if (cached) {
		return cached;
	}

	const options: SelectOptionConfig<number>[] = [];

	// 1 to 60 seconds: step 1 second
	for (let i = 1; i <= 60; i += 1) {
		options.push({
			value: i,
			label: formatDuration(i),
		});
	}

	// 60 seconds to 5 minutes (300 seconds): step 5 seconds
	for (let i = 65; i <= 300; i += 5) {
		options.push({
			value: i,
			label: formatDuration(i),
		});
	}

	// 5 minutes to 1 hour (3600 seconds): step 15 seconds
	for (let i = 315; i <= 3600; i += 15) {
		options.push({
			value: i,
			label: formatDuration(i),
		});
	}

	// 1 hour to 10 hours (36000 seconds): step 1 minute (60 seconds)
	for (let i = 3660; i <= 36_000; i += 60) {
		options.push({
			value: i,
			label: formatDuration(i),
		});
	}

	setOptionsInCache(cacheKey, options);
	return options;
}

/**
 * Generate rep pace options with variable step sizes and decimal formatting.
 *
 * Ranges:
 * - 1-60 seconds: 0.5 second steps with decimals (119 options) - e.g., "00:01.5", "00:30.5"
 * - 1-5 minutes: 1 second steps without decimals (240 options) - e.g., "01:15", "03:00"
 *
 * Total: ~359 options
 */
export function generateRepPaceOptions(): SelectOptionConfig<number>[] {
	const cacheKey = 'rep-pace';
	const cached = getOptionsFromCache<number>(cacheKey);

	if (cached) {
		return cached;
	}

	const options: SelectOptionConfig<number>[] = [];

	// 1 to 60 seconds: step 0.5 seconds
	for (let i = 1; i <= 60; i += 0.5) {
		const value = Number(i.toFixed(1));
		options.push({
			value,
			label: formatDuration(value),
		});
	}

	// 1 minute to 5 minutes (300 seconds): step 1 second
	for (let i = 61; i <= 300; i += 1) {
		options.push({
			value: i,
			label: formatDuration(i),
		});
	}

	setOptionsInCache(cacheKey, options);
	return options;
}

/**
 * Generate distance options with variable step sizes.
 *
 * Ranges:
 * - 1-100: 1 step (100 options)
 * - 100-1000: 10 step (90 options)
 * - 1000-50000: 100 step with thousand separators (490 options)
 *
 * Total: ~680 options
 */
export function generateDistanceOptions(): SelectOptionConfig<number>[] {
	const cacheKey = 'distance';
	const cached = getOptionsFromCache<number>(cacheKey);

	if (cached) {
		return cached;
	}

	const options: SelectOptionConfig<number>[] = [];

	// 1 to 100: step 1
	for (let i = 1; i <= 100; i += 1) {
		options.push({
			value: i,
			label: i.toString(),
		});
	}

	// 100 to 1000: step 10
	for (let i = 110; i <= 1000; i += 10) {
		options.push({
			value: i,
			label: i.toString(),
		});
	}

	// 1000 to 50000: step 100
	for (let i = 1100; i <= 50_000; i += 100) {
		options.push({
			value: i,
			label: formatDistanceNumber(i),
		});
	}

	setOptionsInCache(cacheKey, options);
	return options;
}

/**
 * Generate height options with variable step sizes.
 *
 * Ranges:
 * - 1-100: 1 step (100 options)
 * - 100-1000: 5 step (40 options)
 *
 * Total: ~140 options
 */
export function generateHeightOptions(): SelectOptionConfig<number>[] {
	const cacheKey = 'height';
	const cached = getOptionsFromCache<number>(cacheKey);

	if (cached) {
		return cached;
	}

	const options: SelectOptionConfig<number>[] = [];

	// 1 to 100: step 1
	for (let i = 1; i <= 100; i += 1) {
		options.push({
			value: i,
			label: i.toString(),
		});
	}

	// 100 to 1000: step 5
	for (let i = 105; i <= 1000; i += 5) {
		options.push({
			value: i,
			label: i.toString(),
		});
	}

	setOptionsInCache(cacheKey, options);
	return options;
}

/**
 * Generate weight options with 0.5 step, hiding .0 decimals.
 *
 * Range: 0-300kg with 0.5 step (600 options)
 * Format: Shows "22.5" but "23" (not "23.0")
 */
export function generateWeightOptions(): SelectOptionConfig<number>[] {
	const cacheKey = 'weight';
	const cached = getOptionsFromCache<number>(cacheKey);

	if (cached) {
		return cached;
	}

	const options: SelectOptionConfig<number>[] = [];

	// 0 to 300: step 0.5
	for (let i = 0.5; i <= 300; i += 0.5) {
		const value = Number(i.toFixed(1));
		// Hide .0 decimals: 23.0 becomes "23", 22.5 stays "22.5"
		const label = value % 1 === 0 ? value.toString() : value.toFixed(1);
		options.push({
			value,
			label,
		});
	}

	setOptionsInCache(cacheKey, options);
	return options;
}

export function generateSectionTypeOptions(): SelectOptionConfig<SectionType>[] {
	const cacheKey = 'section-type';
	const cached = getOptionsFromCache<SectionType>(cacheKey);
	if (cached) {
		return cached;
	}

	const options: SelectOptionConfig<SectionType>[] = [];
	for (const sectionType of sectionTypeDisplayInfosArray) {
		options.push({
			value: sectionType.coreModel.canonicalName,
			label: sectionType.displayName,
		});
	}
	setOptionsInCache(cacheKey, options);
	return options;
}

/**
 * Format distance numbers with thousand separators for values >= 1000.
 * Examples: 1000 → "1,000", 5000 → "5,000", 25000 → "25,000"
 */
export function formatDistanceNumber(value: number): string {
	if (value >= 1000) {
		return value.toLocaleString('en-US');
	}
	return value.toString();
}

// /**
//  * Generate options from a range configuration with caching.
//  */
// export function generateOptionsFromRange(
//   config: RangeConfig,
//   cacheKey: string,
// ): SelectOptionConfig[] {
//   if (OPTIONS_CACHE.has(cacheKey)) {
//     return OPTIONS_CACHE.get(cacheKey)!;
//   }

//   const options: SelectOptionConfig[] = [];

//   for (let i = config.min; i <= config.max; i += config.step) {
//     const value = Number(i.toFixed(config.precision));
//     const label = config.displayAs === 'time'
//       ? formatDuration(value)
//       : (config.precision > 0 ? value.toFixed(config.precision) : value.toString());

//     options.push({ value, label });
//   }

//   OPTIONS_CACHE.set(cacheKey, options);
//   return options;
// }

/**
 * Clear the options cache (useful for testing or memory management)
 */
export function clearOptionsCache(): void {
	OPTIONS_CACHE.clear();
}
